import { Tooltip } from "antd";
import { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  ICreateTriggerProps,
  ITriggerProps,
} from "../../../../../../@types/Alerts";
import { IUserPreferences } from "../../../../../../@types/Preferences";
import { BlackMinus } from "../../../../../../assets/icons/BlackMinus";
import { BlackPlus } from "../../../../../../assets/icons/BlackPlus";
import { Button } from "../../../../../../components/Button";
import { SelectDropdown } from "../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../components/TextField";
import { persistedCopyT } from "../../../../../../config/persistedCopy";
import { AUTH_UNIT_PREFERENCES } from "../../../../../../constants/localStorageKeys";
import { alertsService } from "../../../../../../services/alertsService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../../../../utils/storage";
import { TRIGGER_TYPE_LABEL_KEYS } from "../../../../../../utils/triggerLabels";
import {
  buildTriggerSummarySentence,
  getTriggerUnit,
} from "../../../../../../utils/triggerSummary";
import { CancelButton } from "../ManageTriggersModal/ManageTriggersModal.styles";
import {
  AdditionalButton,
  Container,
  ContentContainer,
  Field,
  HCPMessage,
  NoteSection,
  ScrollableContainer,
  SelectSection,
  SubmitContainer,
  Title,
} from "./CreateTriggerModal.styles";

type CreateTriggerModalProps = {
  content?: ITriggerProps;
  isEdit?: boolean;
  measurementAlertId?: string;
  patientId: number;
  setParentNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
  onClose: () => void;
};

/**
 * Comparators, in the order the dropdown shows them.
 *
 * `value` is the identity: it is what the form state holds, what `handleComparator` turns into
 * the `>`/`<`/`+` the payload carries, and what an existing trigger is read back as. Only
 * `labelKey` is language-dependent, so translating a comparator can never change which alert
 * fires (EN-5191).
 */
const COMPARATORS = [
  {
    value: "weight_gain",
    labelKey: "patientPage_triggers_comparator_weightGain",
    disabled: true,
  },
  {
    value: "greater_than",
    labelKey: "patientPage_triggers_comparator_greaterThan",
    disabled: false,
  },
  {
    value: "greater_or_equal",
    labelKey: "patientPage_triggers_comparator_greaterOrEqual",
    disabled: false,
  },
  {
    value: "less_than",
    labelKey: "patientPage_triggers_comparator_lessThan",
    disabled: false,
  },
  {
    value: "less_or_equal",
    labelKey: "patientPage_triggers_comparator_lessOrEqual",
    disabled: false,
  },
  {
    value: "percentage_decrease",
    labelKey: "patientPage_triggers_comparator_percentageDecrease",
    disabled: false,
  },
  {
    value: "percentage_increase",
    labelKey: "patientPage_triggers_comparator_percentageIncrease",
    disabled: false,
  },
];

// EN-5191: factories, not module constants — a constant is evaluated at import time, before
// i18next is initialised, and every label would come back undefined.
const buildComparatorOptions = (t: TFunction) =>
  COMPARATORS.map(({ value, labelKey, disabled }) => ({
    label: t(labelKey),
    value,
    key: uuidv4(),
    disabled,
  }));

const buildTypeOptions = (t: TFunction) =>
  Object.entries(TRIGGER_TYPE_LABEL_KEYS).map(([value, labelKey]) => ({
    label: t(labelKey),
    value,
    key: uuidv4(),
  }));

const buildOptionsAlerts = (t: TFunction) => [
  {
    label: t("patientPage_triggers_trigger_anyReading"),
    value: "any_reading",
    key: uuidv4(),
  },
  {
    label: t("patientPage_triggers_trigger_highestInDay"),
    value: "highest_of_day",
    key: uuidv4(),
  },
  {
    label: t("patientPage_triggers_trigger_consecutive3"),
    value: "consecutive_highest_3_days",
    key: uuidv4(),
  },
  {
    label: t("patientPage_triggers_trigger_consecutive7"),
    value: "consecutive_highest_7_days",
    key: uuidv4(),
  },
];

const buildOptionsNotification = (t: TFunction) => [
  {
    label: t("patientPage_triggers_notify_email"),
    value: "Email",
    key: uuidv4(),
  },
  { label: t("patientPage_triggers_notify_sms"), value: "SMS", key: uuidv4() },
  {
    label: t("patientPage_triggers_notify_call"),
    value: "Call",
    key: uuidv4(),
  },
];

export function CreateTriggerModal({
  content,
  isEdit = false,
  measurementAlertId,
  setParentNotification,
  onClose,
  patientId,
}: CreateTriggerModalProps) {
  const { t } = useTranslation();
  const optionsAlertsList = useMemo(() => buildOptionsAlerts(t), [t]);
  const optionsNotificationList = useMemo(
    () => buildOptionsNotification(t),
    [t]
  );
  const optionsType = useMemo(() => buildTypeOptions(t), [t]);

  /**
   * The summary is also **persisted** — it goes out as `hcpMessage` with the trigger and is what
   * every other user, and the alert notification, reads back. So it is always written in en-GB,
   * whatever language the author is working in: otherwise the same trigger would read
   * differently depending on who happened to create it.
   *
   * From its own isolated instance, not `i18n.getFixedT("en-GB")`. That reads the live store,
   * which ?i18nDebug=1, ?pseudo=1 and the published en-GB overlay all rewrite — a trigger saved
   * in either debug mode persisted the decorated string for everyone. See config/persistedCopy.
   */

  const [thresholdUnit, setThresholdUnit] = useState("");
  const [thresholdUnit2, setThresholdUnit2] = useState("");
  const [additionalMeasurement, setAdditionalMeasurement] = useState(false);

  const [type, setType] = useState("");
  const [comparator, setComparator] = useState("");
  const [threshold, setThreshold] = useState("");
  const [percentage, setPercentage] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [type2, setType2] = useState("");
  const [comparator2, setComparator2] = useState("");
  const [threshold2, setThreshold2] = useState("");
  const [percentage2, setPercentage2] = useState("");
  const [timeframe2, setTimeframe2] = useState("");
  const [alert, setAlert] = useState("");
  const [notification, setNotification] = useState<string[]>([]);
  const [hcpMessage, setHcpMessage] = useState("");

  // Only the weight-gain row's availability changes at runtime, so the flag is the state and the
  // option list is derived — that way the labels follow the language instead of being frozen at
  // the value the list happened to be built with.
  const [isWeightGainDisabled, setIsWeightGainDisabled] = useState(true);

  const baseComparatorsOptions = useMemo(() => buildComparatorOptions(t), [t]);

  const optionsComparator = useMemo(
    () =>
      baseComparatorsOptions.map((comparator) =>
        comparator.value === "weight_gain"
          ? { ...comparator, disabled: isWeightGainDisabled }
          : comparator
      ),
    [baseComparatorsOptions, isWeightGainDisabled]
  );

  const setWeightGainDisabledStatus = (status: boolean) => {
    setIsWeightGainDisabled(status);
  };

  let mounted = true;

  const handleSetAlert = (days: number | undefined, comparator?: string) => {
    if (comparator === "+" && days !== undefined) {
      setTimeframe(days.toString());
      return;
    }

    switch (days) {
      case 1:
        setAlert("highest_of_day");
        break;
      case 3:
        setAlert("consecutive_highest_3_days");
        break;
      case 7:
        setAlert("consecutive_highest_7_days");
        break;
      default:
        setAlert("any_reading");
        break;
    }
  };

  const handleSetComparator = (comparator: string, isPercent: boolean) => {
    switch (comparator) {
      case ">":
        if (isPercent) {
          return "percentage_increase";
        }
        return "greater_than";
      case "<":
        if (isPercent) {
          return "percentage_decrease";
        }
        return "less_than";
      case ">=":
        return "greater_or_equal";
      case "<=":
        return "less_or_equal";
      case "+":
        setWeightGainDisabledStatus(false);
        return "weight_gain";
      default:
        return "";
    }
  };

  const handleThresholdUnit = (value: string, isSecondTrigger: boolean) => {
    if (!mounted) return;

    const setUnit = (value: string) => {
      if (isSecondTrigger) {
        setThresholdUnit2(value);
        return;
      }
      setThresholdUnit(value);
    };

    const userUnitPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);

    const parsedUserUnitPreferences = safeJsonParse<
      IUserPreferences | undefined
    >(userUnitPreferences, undefined);

    // A measurement the form does not offer leaves the unit as it was.
    const unit = getTriggerUnit(value, parsedUserUnitPreferences?.weightUnit);

    if (unit !== undefined) {
      setUnit(unit);
    }
  };

  useEffect(() => {
    mounted = true;

    if (content !== undefined && content !== null && isEdit && mounted) {
      setType(content.measurementField);
      setNotification(
        content.hcpNotification !== undefined
          ? content.hcpNotification.split(", ")
          : []
      );
      setComparator(
        handleSetComparator(content.comparator, content.baseline !== undefined)
      );
      handleSetAlert(content.timeframeDays, content.comparator);
      handleThresholdUnit(content.measurementField, false);

      if (
        content.percentChange !== undefined &&
        content.baseline !== undefined
      ) {
        setThreshold(content.baseline.toString());
        setPercentage(content.percentChange.toString());
      } else {
        setThreshold(
          content.threshold !== undefined ? content.threshold.toString() : ""
        );
      }

      if (content.hasSecondTrigger) {
        setAdditionalMeasurement(true);
        setType2(
          content.measurementField2 !== undefined
            ? content.measurementField2
            : ""
        );
        setComparator2(
          handleSetComparator(
            content.comparator2 !== undefined ? content.comparator2 : "",
            content.baseline !== undefined
          )
        );
        handleThresholdUnit(
          content.measurementField2 !== undefined
            ? content.measurementField2.toString()
            : "",
          true
        );

        if (
          content.percentChange2 !== undefined &&
          content.baseline2 !== undefined
        ) {
          setThreshold2(content.baseline2.toString());
          setPercentage2(content.percentChange2.toString());
        } else {
          setThreshold2(
            content.threshold2 !== undefined
              ? content.threshold2.toString()
              : ""
          );
        }
      }
    }

    return () => {
      mounted = false;
    };
  }, [content, isEdit]);

  const handleTimeFrame = () => {
    if (comparator === "weight_gain") {
      return parseInt(timeframe, 10);
    }

    switch (alert) {
      case "highest_of_day":
        return 1;
      case "consecutive_highest_3_days":
        return 3;
      case "consecutive_highest_7_days":
        return 7;
      default:
        return undefined;
    }
  };

  const handleComparator = (secondTrigger: boolean) => {
    switch (!secondTrigger ? comparator : comparator2) {
      case "weight_gain":
        return "+";
      case "greater_than":
        return ">";
      case "greater_or_equal":
        return ">=";
      case "less_than":
        return "<";
      case "less_or_equal":
        return "<=";
      case "percentage_decrease":
        return "<";
      case "percentage_increase":
        return ">";
      default:
        return "";
    }
  };

  const handleCreateTrigger = () => {
    const dataToCreateTrigger: ICreateTriggerProps = {
      measurementType: type,
      unit: thresholdUnit,
      comparator: handleComparator(false),
      threshold:
        comparator !== "percentage_decrease" &&
        comparator !== "percentage_increase"
          ? Number.parseFloat(threshold)
          : undefined,
      baseline:
        comparator === "percentage_decrease" ||
        comparator === "percentage_increase"
          ? Number.parseFloat(threshold)
          : undefined,
      percent:
        comparator === "percentage_decrease" ||
        comparator === "percentage_increase"
          ? Number.parseFloat(percentage)
          : undefined,
      secondTrigger: additionalMeasurement,
      measurementType2: additionalMeasurement ? type2 : undefined,
      comparator2: additionalMeasurement ? handleComparator(true) : undefined,
      threshold2:
        additionalMeasurement &&
        comparator2 !== "percentage_decrease" &&
        comparator2 !== "percentage_increase"
          ? Number.parseFloat(threshold2)
          : undefined,
      baseline2:
        additionalMeasurement &&
        (comparator2 === "percentage_decrease" ||
          comparator2 === "percentage_increase")
          ? Number.parseFloat(threshold2)
          : undefined,
      percent2:
        additionalMeasurement &&
        (comparator2 === "percentage_decrease" ||
          comparator2 === "percentage_increase")
          ? Number.parseFloat(percentage2)
          : undefined,
      hcpNotification: notification,
      hcpMessage,
      timeFrameDays: handleTimeFrame(),
      patientId,
    };

    if (isEdit) {
      alertsService
        .editTrigger(dataToCreateTrigger, measurementAlertId)
        .then((editedSuccessfully) => {
          const { data } = editedSuccessfully;

          if (data) {
            setParentNotification(
              t("patientPage_triggers_create_edited"),
              "success"
            );
          } else {
            setParentNotification(
              t("patientPage_triggers_create_editFailed"),
              "error"
            );
          }

          onClose();
        });
    } else {
      alertsService
        .createTrigger(dataToCreateTrigger)
        .then((createdSuccessfully) => {
          const { data } = createdSuccessfully;

          if (data) {
            if (type === "weight" && comparator === "weight_gain") {
              mixpanelActions.track("User Action: Weight gain alert created");
            }

            setParentNotification(
              t("patientPage_triggers_create_created"),
              "success"
            );
          } else {
            setParentNotification(
              t("patientPage_triggers_create_createFailed"),
              "error"
            );
          }

          onClose();
        });
    }
  };

  const handleCancelClick = () => {
    onClose();
  };

  const handleValueChange = (
    value: string | string[],
    select: string,
    secondTrigger = false
  ) => {
    if (!mounted) return;

    if (typeof value === "string") {
      if (select === "type") {
        if (!secondTrigger && type !== value) {
          if (value === "weight") {
            setWeightGainDisabledStatus(false);

            setType("");
          } else {
            setWeightGainDisabledStatus(true);
          }

          setType(value);
          setComparator("");
          setPercentage("");
          setThreshold("");
          handleThresholdUnit(value, false);
          return;
        }

        if (secondTrigger && type2 !== value) {
          setType2(value);
          setComparator2("");
          setPercentage2("");
          setThreshold2("");
          handleThresholdUnit(value, true);
          return;
        }
      }
      if (select === "comparator") {
        if (!secondTrigger && comparator !== value) {
          setComparator(value);
          return;
        }
        if (secondTrigger && comparator2 !== value) {
          setComparator2(value);
          return;
        }
      }
      if (select === "threshold") {
        if (!secondTrigger && threshold !== value) {
          setThreshold(value);
          return;
        }
        if (secondTrigger && threshold2 !== value) {
          setThreshold2(value);
          return;
        }
      }
      if (select === "alert" && alert !== value) {
        setAlert(value);
        return;
      }
      if (select === "percentage") {
        const parsesdNumber = Number.parseInt(value, 10);
        if ((parsesdNumber >= 0 && parsesdNumber <= 100) || value === "") {
          if (!secondTrigger && percentage !== value) {
            setPercentage(value);
            return;
          }
          if (secondTrigger && percentage2 !== value) {
            setPercentage2(value);
            return;
          }
        }
      }
      if (select === "timeframe") {
        if (!secondTrigger && timeframe !== value) {
          setTimeframe(value);
          return;
        }
        if (secondTrigger && timeframe2 !== value) {
          setTimeframe2(value);
        }
      }
    } else {
      setNotification(value);
    }
  };

  const buildSummarySentence = (translate: TFunction): string =>
    buildTriggerSummarySentence(translate, {
      type,
      comparator,
      threshold,
      percentage,
      timeframe,
      alert,
      unit: thresholdUnit,
      additionalMeasurement,
      type2,
      comparator2,
      threshold2,
      percentage2,
      unit2: thresholdUnit2,
    });

  const buildHCPMessage = () => {
    if (!mounted) return "";

    const english = buildSummarySentence(persistedCopyT);

    if (hcpMessage !== english) {
      setHcpMessage(english);
    }

    return buildSummarySentence(t);
  };

  const handleDisableButton = () => {
    if (!additionalMeasurement) {
      if (comparator === "weight_gain") {
        return (
          type.length === 0 ||
          comparator.length === 0 ||
          threshold.length === 0 ||
          timeframe.length === 0
        );
      }

      return (
        type.length === 0 ||
        comparator.length === 0 ||
        threshold.length === 0 ||
        (comparator !== "percentage_decrease" &&
        comparator !== "percentage_increase"
          ? false
          : percentage.length === 0) ||
        alert.length === 0
      );
    }

    return (
      type.length === 0 ||
      comparator.length === 0 ||
      threshold.length === 0 ||
      (comparator !== "percentage_decrease" &&
      comparator !== "percentage_increase"
        ? false
        : percentage.length === 0) ||
      alert.length === 0 ||
      type2.length === 0 ||
      threshold2.length === 0 ||
      (comparator2 !== "percentage_decrease" &&
      comparator2 !== "percentage_increase"
        ? false
        : percentage2.length === 0)
    );
  };

  return (
    <Container>
      <Title>
        {isEdit
          ? t("patientPage_triggers_create_titleEdit")
          : t("patientPage_triggers_create_titleCreate")}{" "}
      </Title>
      <ScrollableContainer>
        <ContentContainer>
          <SelectSection>
            <Field>
              <p>{t("patientPage_triggers_create_type")}</p>
              <SelectDropdown
                options={optionsType}
                placeholder={t("patientPage_triggers_create_typePlaceholder")}
                value={type === "" ? undefined : type}
                onValueChange={(value: string | string[]) => {
                  handleValueChange(value, "type");
                }}
              />
            </Field>

            {type !== "" ? (
              <Field>
                <p>{t("patientPage_triggers_create_comparator")}</p>
                <SelectDropdown
                  options={optionsComparator.filter(
                    (comparator) => !comparator.disabled
                  )}
                  placeholder={t("patientPage_triggers_comparator_greaterThan")}
                  value={comparator === "" ? undefined : comparator}
                  onValueChange={(value: string | string[]) => {
                    handleValueChange(value, "comparator");
                  }}
                />
              </Field>
            ) : null}

            {comparator !== "" ? (
              <>
                {comparator === "percentage_decrease" ||
                comparator === "percentage_increase" ? (
                  <Field>
                    <TextField
                      placeholder="0"
                      label={t(
                        comparator === "percentage_decrease"
                          ? "patientPage_triggers_create_percentageDecrease"
                          : "patientPage_triggers_create_percentageIncrease"
                      )}
                      value={percentage ?? ""}
                      onChange={(event: any) => {
                        handleValueChange(event.target.value, "percentage");
                      }}
                      backgroudColor="white"
                      type="number"
                    />
                  </Field>
                ) : null}
                {comparator !== "weight_gain" ? (
                  <>
                    <Field>
                      <TextField
                        placeholder="0"
                        label={t(
                          comparator === "percentage_decrease" ||
                            comparator === "percentage_increase"
                            ? "patientPage_triggers_create_baseline"
                            : "patientPage_triggers_create_threshold",
                          { unit: thresholdUnit }
                        )}
                        value={threshold ?? ""}
                        onChange={(event: any) => {
                          handleValueChange(event.target.value, "threshold");
                        }}
                        backgroudColor="white"
                        type="number"
                      />
                    </Field>
                    <Field>
                      <p>{t("patientPage_triggers_create_alertTriggers")}</p>
                      <SelectDropdown
                        options={optionsAlertsList}
                        placeholder={t(
                          "patientPage_triggers_create_alertTriggersPlaceholder"
                        )}
                        value={alert ?? ""}
                        onValueChange={(value: string | string[]) => {
                          handleValueChange(value, "alert");
                        }}
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field>
                      <TextField
                        placeholder="0"
                        label={t("patientPage_triggers_create_weightChange", {
                          unit: thresholdUnit,
                        })}
                        value={threshold ?? ""}
                        onChange={(event: any) => {
                          handleValueChange(event.target.value, "threshold");
                        }}
                        backgroudColor="white"
                        type="number"
                      />
                    </Field>
                    <Field>
                      <TextField
                        placeholder="1"
                        label={t("patientPage_triggers_create_timeframe")}
                        value={timeframe ?? ""}
                        onChange={(event: any) => {
                          if (
                            parseInt(event.target.value, 10) < 1 ||
                            parseInt(event.target.value, 10) > 30
                          ) {
                            return;
                          }

                          handleValueChange(event.target.value, "timeframe");
                        }}
                        backgroudColor="white"
                        type="number"
                      />
                    </Field>
                  </>
                )}
                <Field>
                  <p>{t("patientPage_triggers_create_hcpNotification")}</p>
                  <SelectDropdown
                    options={optionsNotificationList}
                    placeholder={t(
                      "patientPage_triggers_create_hcpNotificationPlaceholder"
                    )}
                    multiple
                    value={notification ?? ""}
                    onValueChange={(value: string | string[]) => {
                      handleValueChange(value, "notification");
                    }}
                  />
                </Field>
              </>
            ) : null}
          </SelectSection>

          {!additionalMeasurement &&
          type !== "" &&
          comparator !== "" &&
          (alert !== "" || timeframe !== "") &&
          threshold !== "" ? (
            <Field isHCPMessage>
              <p>{t("patientPage_triggers_create_summary")}</p>
              <HCPMessage>{buildHCPMessage()}</HCPMessage>
            </Field>
          ) : null}
        </ContentContainer>

        {additionalMeasurement ? (
          <>
            <p
              style={{
                margin: "5px 0 5px 50px",
                color: "#24484A",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {t("patientPage_triggers_create_and")}
            </p>

            <ContentContainer>
              <SelectSection>
                <Field>
                  <p>{t("patientPage_triggers_create_type")}</p>
                  <SelectDropdown
                    options={optionsType}
                    placeholder={t(
                      "patientPage_triggers_create_typePlaceholder"
                    )}
                    value={type2 === "" ? undefined : type2}
                    onValueChange={(value: string | string[]) => {
                      handleValueChange(value, "type", true);
                    }}
                  />
                </Field>
                {type2 !== "" ? (
                  <Field>
                    <p>{t("patientPage_triggers_create_comparator")}</p>
                    <SelectDropdown
                      options={baseComparatorsOptions.filter(
                        (comparator) => !comparator.disabled
                      )}
                      placeholder={t(
                        "patientPage_triggers_comparator_greaterThan"
                      )}
                      value={comparator2 === "" ? undefined : comparator2}
                      onValueChange={(value: string | string[]) => {
                        handleValueChange(value, "comparator", true);
                      }}
                    />
                  </Field>
                ) : null}
                {comparator2 !== "" ? (
                  <>
                    {comparator2 === "percentage_decrease" ||
                    comparator2 === "percentage_increase" ? (
                      <Field>
                        <TextField
                          placeholder="0"
                          label={t(
                            comparator2 === "percentage_decrease"
                              ? "patientPage_triggers_create_percentageDecrease"
                              : "patientPage_triggers_create_percentageIncrease"
                          )}
                          value={percentage2 ?? ""}
                          onChange={(event: any) => {
                            handleValueChange(
                              event.target.value,
                              "percentage",
                              true
                            );
                          }}
                          backgroudColor="white"
                          type="number"
                        />
                      </Field>
                    ) : null}
                    <Field>
                      <TextField
                        placeholder="0"
                        label={t(
                          comparator2 === "percentage_decrease" ||
                            comparator2 === "percentage_increase"
                            ? "patientPage_triggers_create_baseline"
                            : "patientPage_triggers_create_threshold",
                          { unit: thresholdUnit2 }
                        )}
                        value={threshold2 ?? ""}
                        onChange={(event: any) => {
                          handleValueChange(
                            event.target.value,
                            "threshold",
                            true
                          );
                        }}
                        backgroudColor="white"
                        type="number"
                      />
                    </Field>
                  </>
                ) : null}
              </SelectSection>

              {comparator !== "" &&
              type !== "" &&
              alert !== "" &&
              threshold !== "" &&
              additionalMeasurement ? (
                <Field isHCPMessage>
                  <p>{t("patientPage_triggers_create_summary")}</p>
                  <HCPMessage>{buildHCPMessage()}</HCPMessage>
                </Field>
              ) : null}
            </ContentContainer>
          </>
        ) : null}

        <Tooltip
          title={
            comparator === "weight_gain"
              ? t("patientPage_triggers_create_weightGainBlocked")
              : ""
          }
          zIndex={3002}
          color="#4B4B4B"
          styles={{
            body: {
              minHeight: "40px",
              minWidth: "220px",
              borderRadius: "4px !important",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            },
          }}
        >
          <AdditionalButton
            onClick={() => {
              if (comparator !== "weight_gain") {
                setWeightGainDisabledStatus(!additionalMeasurement);
                setAdditionalMeasurement(!additionalMeasurement);
              }
            }}
            disabled={comparator === "weight_gain"}
          >
            {!additionalMeasurement ? (
              <>
                <BlackPlus />
                <p>{t("patientPage_triggers_create_addMeasurement")}</p>
              </>
            ) : (
              <>
                <BlackMinus />
                <p>{t("patientPage_triggers_create_removeMeasurement")} </p>
              </>
            )}
          </AdditionalButton>
        </Tooltip>

        {additionalMeasurement ? (
          <NoteSection>
            <p style={{ fontWeight: "600" }}>
              {t("patientPage_triggers_create_noteTitle")}
            </p>
            <p>
              <Trans
                i18nKey="patientPage_triggers_create_noteBody"
                components={{ bold: <b style={{ fontWeight: "500" }} /> }}
              />
            </p>
          </NoteSection>
        ) : null}
      </ScrollableContainer>

      <SubmitContainer>
        <Button
          label={t("patientPage_triggers_create_save")}
          type="button"
          onClick={handleCreateTrigger}
          disabled={handleDisableButton()}
        />
        <CancelButton onClick={handleCancelClick}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
